/**
 * WP-34 / MR-04 — THE OVERFLOW IS DELIBERATE; THE MISSING SIGNAL IS THE DEFECT.
 *
 * Three strips on the dashboard scroll horizontally on a phone and hide their
 * scrollbar while doing it — `scrollbar-width: none` plus
 * `::-webkit-scrollbar { display: none }` — with nothing put in its place. The
 * KPI strip is an intentional snap carousel (its 32% peek is the design, not a
 * bug); the chip rows are deliberate nowrap strips. What none of them says is
 * "there is more this way".
 *
 * So this module is the signal, not a redesign. Two pieces:
 *
 *   · `useScrollEdges` — whether a scroller can scroll at all, and whether it is
 *     against either end. Everything else reads off that.
 *   · `ScrollShade` — an edge fade that appears only on the side that has more.
 *     A fade on a strip that cannot scroll is furniture; a fade that is always
 *     on both sides teaches nothing.
 *   · `SnapDots` — for the snap carousel, where the reader also wants to know
 *     WHERE they are, not only that there is more.
 *
 * ALL OF IT IS DECORATION FOR THE EYE, and marked `aria-hidden`. A screen
 * reader already reaches every tile and every chip by walking the list; a dot
 * that announces "3 of 6" would be a second, worse way to say what the list
 * already says. The scroll container itself is what gets the keyboard
 * treatment — see `Scroller` below.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { palette, radius, spacing, transition } from '../../style-utils/theme';

export interface ScrollEdges {
  /** The content is wider than the box — there is something to scroll to. */
  canScroll: boolean;
  atStart: boolean;
  atEnd: boolean;
}

/**
 * Watches a horizontal scroller and reports where it is.
 *
 * Re-measures on scroll, on resize, AND when the content changes — a strip that
 * gains a seventh tile after a filter change must not keep saying it has
 * nothing more to show. ResizeObserver covers the last case without a
 * dependency on the caller remembering to tell us.
 */
export const useScrollEdges = (
  ref: React.RefObject<HTMLElement>
): ScrollEdges => {
  const [edges, setEdges] = useState<ScrollEdges>({
    canScroll: false,
    atStart: true,
    atEnd: true,
  });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    // A pixel of slack: sub-pixel layout means scrollLeft rarely lands exactly
    // on 0 or on the maximum, and a fade that flickers at the end of a swipe is
    // worse than one that turns off a pixel early.
    const max = el.scrollWidth - el.clientWidth;
    setEdges({
      canScroll: max > 1,
      atStart: el.scrollLeft <= 1,
      atEnd: el.scrollLeft >= max - 1,
    });
  }, [ref]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    measure();
    el.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);

    // jsdom has no ResizeObserver, and a test that renders this must not crash
    // over an affordance.
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(el);

    /* AND THE CONTENT, BECAUSE THAT IS THE THING THAT GROWS.
       A ResizeObserver on the scroller alone fires when the SCROLLER's box
       changes — which for a width:100% box means "when the viewport does". The
       chip rows and the KPI strip work that way, so this was never noticed. The
       dashboard's content column does NOT: its box is the same size all along
       and its CONTENT goes from empty to a 1082px-wide table once the list has
       loaded. canScroll therefore stayed false over a column overflowing by
       507px, measured on Staff Access at 768.

       A MutationObserver rather than a second ResizeObserver: the node that
       grows is not reliably the first child — a table three levels down does it
       just as often — and watching the subtree for added nodes catches every
       shape of "the content arrived" without having to know which node it is.
       Structure only; attributes and text are excluded. */
    const grew =
      typeof MutationObserver !== 'undefined' ? new MutationObserver(measure) : null;
    grew?.observe(el, { childList: true, subtree: true });

    return () => {
      el.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      observer?.disconnect();
      grew?.disconnect();
    };
  }, [ref, measure]);

  return edges;
};

/**
 * The positioning context a shade needs.
 *
 * `min-width: 0` because these all sit inside flex and grid parents that would
 * otherwise refuse to let them shrink — which is how a strip ends up pushing the
 * page wide instead of scrolling.
 */
export const ScrollFrame = styled.div`
  position: relative;
  min-width: 0;
`;

/** The fade itself. Sits over the scroller, never takes a pointer event. */
export const ScrollShade = styled.span<{ side: 'start' | 'end'; $on: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  width: ${spacing.lg};
  pointer-events: none;
  opacity: ${({ $on }) => ($on ? 1 : 0)};
  transition: ${transition.colors};
  ${({ side }) => (side === 'start' ? 'left: 0;' : 'right: 0;')}
  background: linear-gradient(
    to ${({ side }) => (side === 'start' ? 'right' : 'left')},
    ${palette.surface},
    rgba(255, 255, 255, 0)
  );
`;

const DotRow = styled.div`
  display: flex;
  justify-content: center;
  gap: ${spacing.half};
  padding-top: ${spacing.half};
`;

const Dot = styled.span<{ $on: boolean }>`
  width: ${({ $on }) => ($on ? '18px' : '6px')};
  height: 6px;
  border-radius: ${radius.pill};
  background: ${({ $on }) => ($on ? palette.iris : palette.line)};
  transition: ${transition.colors};
`;

/**
 * Where you are in a snap carousel.
 *
 * The index is derived from the scroll position rather than tracked separately,
 * because the carousel is scrolled by FINGER — there is no event that says "the
 * user chose tile 3", only a scroll offset. Deriving it is the only way the dots
 * cannot disagree with what is on screen.
 */
export const SnapDots: React.FC<{
  scroller: React.RefObject<HTMLElement>;
  count: number;
}> = ({ scroller, count }) => {
  const [index, setIndex] = useState(0);
  const { canScroll } = useScrollEdges(scroller);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return undefined;

    const onScroll = () => {
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) {
        setIndex(0);

        return;
      }
      // Position within the scrollable range, mapped onto the tiles. Rounding
      // rather than flooring so a tile that is more than half revealed is the
      // one reported — which is what the reader sees.
      const ratio = el.scrollLeft / max;
      setIndex(Math.round(ratio * (count - 1)));
    };

    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });

    return () => el.removeEventListener('scroll', onScroll);
  }, [scroller, count]);

  if (!canScroll || count < 2) return null;

  return (
    <DotRow aria-hidden="true" data-testid="snap-dots">
      {Array.from({ length: count }, (_, i) => (
        <Dot key={i} $on={i === index} />
      ))}
    </DotRow>
  );
};

/**
 * A horizontal strip with edge fades that follow its scroll position.
 *
 * `children` is the scroller itself — this wraps rather than replaces it, so
 * each strip keeps the layout it was designed with (a snap grid, a flex row of
 * chips) and only gains the signal.
 */
export const ScrollStrip: React.FC<{
  scroller: React.RefObject<HTMLElement>;
  children: React.ReactNode;
  className?: string;
}> = ({ scroller, children, className }) => {
  const { canScroll, atStart, atEnd } = useScrollEdges(scroller);

  return (
    <ScrollFrame className={className}>
      {children}
      {/* `data-on` is not decoration: it is what a test and the live probe can
          read. The opacity lives in an emotion class, and jsdom's
          getComputedStyle does not resolve those — asserting on the style would
          be asserting on nothing. */}
      <ScrollShade
        side="start"
        $on={canScroll && !atStart}
        data-shade="start"
        data-on={canScroll && !atStart}
        aria-hidden="true"
      />
      <ScrollShade
        side="end"
        $on={canScroll && !atEnd}
        data-shade="end"
        data-on={canScroll && !atEnd}
        aria-hidden="true"
      />
    </ScrollFrame>
  );
};

/**
 * The keyboard contract for a scroller that is not itself focusable.
 *
 * A `overflow-x: auto` box with no focusable child is unreachable by keyboard in
 * Firefox and Safari — the content scrolls, but nothing can put the caret in it.
 * These strips are full of buttons, so they are reachable in practice; this is
 * here for the one case that is not, and to keep the decision written down
 * rather than rediscovered.
 */
export const useKeyboardScrollable = (
  ref: React.RefObject<HTMLElement>,
  hasFocusableChildren: boolean
): void => {
  const applied = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || hasFocusableChildren || applied.current) return;
    el.setAttribute('tabindex', '0');
    applied.current = true;
  }, [ref, hasFocusableChildren]);
};
